import ImageBackupModal from "./image.backup.modal";
import DbBackupModal from "./db.backup.modal";
import DeviceBackupModal from "./device.backup.modal";
import type { BackupDateContext } from "./backup.date.list";
import { useState } from "react";
import DeviceWormModal from "./device.worm.modal";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useNavigate, useParams } from "react-router-dom";
import {
  DeviceDeleteTooltip,
  DeviceListContainer,
} from "../../style/contents/backup/backup";
import { DeviceItem } from "../../view/contents/backup/backup.device.list";

interface Props {
  data: DeviceItem[] | null;
  ownerId?: string;
  individualDevices?: boolean;
  backupKind?: "file" | "db" | "image";
  onDelete?: (device: DeviceItem) => void;
  deletingDeviceId?: string | null;
  selectedDevices?: string[];
  onSelectChange?: (deviceId: string, checked: boolean) => void;
  role: string;
  department?: string;
  folderPathPrefix?: string;
}

const DeviceList = ({
  data,
  ownerId,
  onSelectChange,
  selectedDevices = [],
  individualDevices = false,
  backupKind = "file",
  onDelete,
  deletingDeviceId,
  role,
  department,
  folderPathPrefix = "/backup/folder",
}: Props) => {
  const { id } = useParams<{ id: string }>();

  const userId = ownerId ?? id;
  const [wormTarget, setWormTarget] = useState<{
    device: DeviceItem;
    directoryPath: string;
  } | null>(null);

  const [backupTarget, setBackupTarget] = useState<BackupDateContext | null>(
    null,
  );
  const [imageTarget, setImageTarget] = useState<DeviceItem | null>(null);
  const [dbTarget, setDbTarget] = useState<DeviceItem | null>(null);
  const navigate = useNavigate();
  const viewBackupBtn = (
    deviceId: string,
    userAccount: string,
    userName: string,
    department: string,
    pcName: string,
    isAdmin: boolean,
  ) => {
    const encodedDeviceId = encodeURIComponent(deviceId);
    const encodedName = btoa(encodeURIComponent(userName)); // 이름 인코딩
    const encodedDept = btoa(encodeURIComponent(department)); // 부서 인코딩
    const encodedPcName = btoa(encodeURIComponent(pcName)); // PC 이름 인코딩

    navigate(
      `${folderPathPrefix}/${encodedDeviceId}?id=${userId}&userAccount=${userAccount}&name=${encodedName}&department=${encodedDept}&pcName=${encodedPcName}&isAdmin=${isAdmin}`,
    );
  };

  if (!data?.length) {
    return (
      <DeviceListContainer>
        <div className="no-data">등록된 디바이스가 없습니다</div>
      </DeviceListContainer>
    );
  }

  return (
    <Tooltip.Provider delayDuration={200}>
      <DeviceListContainer>
        {data.map((item) => {
          const isChecked = selectedDevices.includes(item.deviceId);

          return (
            <div className="device-items" key={item.deviceId}>
              <div className="device-item">
                <div className="input-box">
                  {role === "admin" && !individualDevices && (
                    <input
                      type="checkbox"
                      id={`device-${userId}-${item.deviceId}`}
                      checked={isChecked}
                      onChange={(e) =>
                        onSelectChange?.(item.deviceId, e.target.checked)
                      }
                    />
                  )}
                  <div className="pc">
                    <img src="/assets/new_design/laptop.png" alt="" />
                  </div>
                  <div className="pc-name-wrap">
                    <div className="btn-wrap">
                      {individualDevices ? (
                        <span className="pc-name">{item.pcName}</span>
                      ) : (
                        <label htmlFor={`device-${userId}-${item.deviceId}`}>
                          {item.pcName}
                        </label>
                      )}
                      {role === "admin" && onDelete && (
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <button
                              type="button"
                              aria-label={`${item.pcName || item.deviceId} 삭제`}
                              disabled={!!deletingDeviceId}
                              onClick={() => onDelete(item)}
                            >
                              <img
                                src="/assets/new_design/del_icon.png"
                                alt=""
                              />
                            </button>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <DeviceDeleteTooltip
                              side="top"
                              sideOffset={6}
                              collisionPadding={8}
                            >
                              디바이스 삭제
                              <Tooltip.Arrow fill="#333333" />
                            </DeviceDeleteTooltip>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      )}
                    </div>
                    <div className="device-info">
                      {role === "admin" && <p>IP : {item.originIp}</p>}
                      <p>MAC Address : {item.deviceId}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="info">
                <div className="user-info">
                  <div className="info_">
                    <span>아이디: </span>
                    <span>{item.userAccount}</span>
                  </div>
                  {/* <span>|</span> */}
                  <div className="info_">
                    <span>부서: </span>
                    <span>{department}</span>
                  </div>
                  {/* <span>|</span> */}
                  <div className="info_">
                    <span>이름: </span>
                    <span>{item.userName ? item.userName : "미입력"}</span>
                  </div>
                </div>
              </div>
              <div className="root-path">
                {backupKind === "db" || backupKind === "image" ? (
                  <div className="path">
                    <div className="view-backup">
                      <button type="button" onClick={() => backupKind === "image" ? setImageTarget(item) : setDbTarget(item)}>
                        백업 보기
                      </button>
                    </div>
                  </div>
                ) : individualDevices ? (
                  (item.rootDirectoryList || []).map((rootPath, index) => (
                    <div className="path" key={`${rootPath}-${index}`}>
                      <p>{rootPath}</p>
                      <div className="view-backup">
                        {role === "admin" && (
                          <button
                            className="worm_"
                            type="button"
                            onClick={() =>
                              setWormTarget({
                                device: item,
                                directoryPath: rootPath,
                              })
                            }
                          >
                            WORM 적용
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setBackupTarget({
                              deviceId: item.deviceId,
                              userId: item.userId,
                              userAccount: item.userAccount,
                              rootDirectoryPath: rootPath,
                              pcName: item.pcName,
                            })
                          }
                        >
                          백업 보기
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="path">
                    <p>{item.rootDirectoryList[0]}</p>
                    <div className="view-backup">
                      <button
                        onClick={() =>
                          viewBackupBtn(
                            item.deviceId,
                            item.userAccount,
                            item.userName || "미입력",
                            department || "-",
                            item.pcName || "미입력",
                            role === "admin",
                          )
                        }
                      >
                        백업 보기
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </DeviceListContainer>
      {role === "admin" && wormTarget && (
        <DeviceWormModal
          device={wormTarget.device}
          directoryPath={wormTarget.directoryPath}
          onClose={() => setWormTarget(null)}
        />
      )}
      {backupTarget && (
        <DeviceBackupModal
          context={backupTarget}
          onClose={() => setBackupTarget(null)}
        />
      )}
      {dbTarget && (
        <DbBackupModal device={dbTarget} onClose={() => setDbTarget(null)} />
      )}
      {imageTarget && <ImageBackupModal device={imageTarget} onClose={() => setImageTarget(null)} />}
    </Tooltip.Provider>
  );
};

export default DeviceList;
